import {
  isElementPhotosGroup,
  PhotosGroup,
} from "@/modes/walk-mode/walk-overlay-selectors";
import { selectIElementsSlice } from "@/store/ielements-slice-selector";
import {
  LightweightElement,
  LightweightElementsRecord,
} from "@/store/lightweight-elements-slice";
import { useAppSelector } from "@/store/store-hooks";
import {
  IElement,
  IElementSection,
  IElementType,
  IElementTypeHint,
  ISOTimeString,
} from "@faro-lotv/ielement-types";
import {
  IElementsRecord,
  SINGLE_SCAN_TYPE_HINTS,
} from "@faro-lotv/project-source";
import { useMemo } from "react";

const SINGLE_SCAN_TYPE_HINTS_SET = new Set<string>(SINGLE_SCAN_TYPE_HINTS);

function isSingleScanTypeHint(typeHint?: string | null): boolean {
  if (!typeHint) return false;
  return SINGLE_SCAN_TYPE_HINTS_SET.has(typeHint);
}

function findAncestorSection(
  element: IElement | LightweightElement,
  elements: LightweightElementsRecord | IElementsRecord,
): IElement | LightweightElement | undefined {
  const parent = element.parentId ? elements[element.parentId] : undefined;

  if (!parent) {
    return;
  }

  if (parent.type === IElementType.section) {
    return parent;
  }

  return findAncestorSection(parent, elements);
}

/**
 * Gets the scan date for an element based on its type and parent section.
 *
 * @param element The element to get the scan date for
 * @param elements The project elements list
 * @param section The parent section of the element
 * @returns The scan date to be displayed for the given element, or undefined if not applicable
 */
export function findScanDate(
  element: IElement | LightweightElement | undefined,
  elements: LightweightElementsRecord | IElementsRecord,
  section?: IElement | LightweightElement | IElementSection,
): ISOTimeString | undefined {
  if (!element || element.type !== IElementType.img360) {
    return;
  }

  // Find ancestor section if not provided
  const resolvedSection = section ?? findAncestorSection(element, elements);

  if (
    resolvedSection &&
    resolvedSection.type === IElementType.section &&
    resolvedSection.typeHint === IElementTypeHint.odometryPath
  ) {
    return;
  }

  // Use the parent section's date if it's a scan
  if (
    resolvedSection &&
    resolvedSection.type === IElementType.section &&
    isSingleScanTypeHint(resolvedSection.typeHint)
  ) {
    return resolvedSection.createdAt;
  }

  // Use the main element's date if it's a normal 360 image
  return element.createdAt;
}

/**
 *
 * @param element current img360 element
 * @param parentSection parent section of the current element
 * @returns the scan date to be displayed for the given element
 */
export function useScanDate(
  element: IElement | PhotosGroup | undefined,
  parentSection?: IElementSection,
): ISOTimeString | undefined {
  const elements = useAppSelector(selectIElementsSlice);

  return useMemo(() => {
    if (!element || isElementPhotosGroup(element)) {
      return;
    }

    return findScanDate(element, elements.iElements, parentSection);
  }, [element, parentSection, elements]);
}
